import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TravelExplore as ToursIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  QrCode as QrCodeIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../components/layout/AdminLayout';
import { toursAPI, travelersAPI, analyticsAPI, apiCall } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [analytics, setAnalytics] = useState(null);
  const [recentTours, setRecentTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch comprehensive analytics and recent tours in parallel
      const [analyticsData, toursData] = await Promise.all([
        apiCall(analyticsAPI.getDashboard),
        apiCall(toursAPI.getAll, { limit: 10 })
      ]);

      setAnalytics(analyticsData.analytics);
      setRecentTours(toursData.tours?.slice(0, 5) || []);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Dashboard - PIMS';
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  if (loading || !analytics) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* Welcome Header */}
        <Box mb={isMobile ? 3 : 4}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            sx={{ 
              fontWeight: 600, 
              mb: 1,
              lineHeight: 1.2,
              wordBreak: 'break-word'
            }}
          >
            Welcome back, {user?.full_name || user?.username}!
          </Typography>
          <Typography 
            variant={isMobile ? "body2" : "body1"} 
            color="text.secondary"
            sx={{ lineHeight: 1.4 }}
          >
            Here's what's happening with your Hajj and Umrah groups today.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        {analytics && (
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Tours
                      </Typography>
                      <Typography variant={isMobile ? "h5" : "h4"} component="h2">
                        {analytics.overview.total_tours}
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        {analytics.overview.active_tours} active
                      </Typography>
                    </Box>
                    <ToursIcon sx={{ 
                      fontSize: isMobile ? 40 : 48, 
                      color: 'primary.main', 
                      opacity: 0.7,
                      ml: 1
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Travelers
                      </Typography>
                      <Typography variant={isMobile ? "h5" : "h4"} component="h2">
                        {analytics.overview.total_travelers}
                      </Typography>
                      <Typography variant="body2" color="info.main">
                        Avg age: {analytics.overview.average_age.toFixed(1)}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ 
                      fontSize: isMobile ? 40 : 48, 
                      color: 'info.main', 
                      opacity: 0.7,
                      ml: 1
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Documents
                      </Typography>
                      <Typography variant={isMobile ? "h5" : "h4"} component="h2">
                        {analytics.documents.completion_rate}%
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Completion Rate
                      </Typography>
                    </Box>
                    <QrCodeIcon sx={{ 
                      fontSize: isMobile ? 40 : 48, 
                      color: 'success.main', 
                      opacity: 0.7,
                      ml: 1
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flex={1}>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Alerts
                      </Typography>
                      <Typography 
                        variant={isMobile ? "h5" : "h4"}
                        component="h2"
                        color={analytics.alerts.passport_expiring > 0 ? 'error.main' : 'text.primary'}
                      >
                        {analytics.alerts.passport_expiring + analytics.alerts.passport_expired}
                      </Typography>
                      <Typography variant="body2" color={analytics.alerts.passport_expiring > 0 ? 'error.main' : 'success.main'}>
                        Passport Issues
                      </Typography>
                    </Box>
                    <WarningIcon sx={{ 
                      fontSize: isMobile ? 40 : 48, 
                      color: analytics.alerts.passport_expiring > 0 ? 'error.main' : 'success.main', 
                      opacity: 0.7,
                      ml: 1
                    }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Analytics Cards */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Gender Distribution
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Male</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {analytics.overview.male_travelers} ({((analytics.overview.male_travelers / analytics.overview.total_travelers) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Female</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {analytics.overview.female_travelers} ({((analytics.overview.female_travelers / analytics.overview.total_travelers) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Recent Activity
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">This Week</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      +{analytics.activity.new_travelers_week}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">This Month</Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      +{analytics.activity.new_travelers_month}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Top Destinations
                  </Typography>
                  {analytics?.destinations?.slice(0, 3).map((dest, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">{dest.destination}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {dest.total_travelers} travelers
                      </Typography>
                    </Box>
                  )) || (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No destination data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Recent Tours and Quick Actions */}
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Recent Tours */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ 
              p: isMobile ? 2 : 3, 
              borderRadius: 3, 
              height: 'fit-content' 
            }}>
              <Box 
                display="flex" 
                alignItems="center" 
                mb={2}
                sx={{ 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 0.5 : 0
                }}
              >
                <Box display="flex" alignItems="center">
                  <ToursIcon sx={{ mr: 1 }} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    component="h2" 
                    sx={{ fontWeight: 600 }}
                  >
                    Recent Tours
                  </Typography>
                </Box>
              </Box>
              
              {recentTours.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  No tours found. Create your first tour to get started.
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentTours.map((tour, index) => (
                    <ListItem 
                      key={tour.id} 
                      divider={index < recentTours.length - 1}
                      sx={{ px: 0 }}
                    >
                      <ListItemText
                        primary={
                          <Box 
                            display="flex" 
                            alignItems={isMobile ? "flex-start" : "center"} 
                            gap={1}
                            sx={{ 
                              flexDirection: isMobile ? "column" : "row",
                              gap: isMobile ? 0.5 : 1
                            }}
                          >
                            <Typography 
                              variant={isMobile ? "body1" : "subtitle1"} 
                              sx={{ 
                                fontWeight: 500,
                                lineHeight: 1.2,
                                wordBreak: 'break-word'
                              }}
                            >
                              {tour.name}
                            </Typography>
                            <Chip 
                              label={tour.status || 'active'} 
                              size="small" 
                              color={tour.status === 'active' ? 'success' : 'default'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: isMobile ? 1 : 0.5 }}>
                            <Typography 
                              variant={isMobile ? "caption" : "body2"} 
                              color="text.secondary"
                              sx={{ 
                                lineHeight: 1.3,
                                wordBreak: 'break-word'
                              }}
                            >
                              {tour.destination} • {formatDate(tour.start_date)} - {formatDate(tour.end_date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tour.traveler_count || 0} travelers
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Quick Stats & Actions */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              p: isMobile ? 2 : 3, 
              borderRadius: 3, 
              height: 'fit-content' 
            }}>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingIcon sx={{ mr: 1 }} />
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  component="h2" 
                  sx={{ fontWeight: 600 }}
                >
                  System Status
                </Typography>
              </Box>
              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2">QR Code System</Typography>
                  <Chip label="Operational" color="success" size="small" />
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2">Database</Typography>
                  <Chip label="Connected" color="success" size="small" />
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2">Authentication</Typography>
                  <Chip label="Active" color="success" size="small" />
                </Box>

                <Box mt={isMobile ? 2 : 3}>
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle2"} 
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Key Features
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    color="text.secondary" 
                    sx={{ mb: 1, lineHeight: 1.4 }}
                  >
                    ✓ Permanent QR codes (UUID-based)
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    color="text.secondary" 
                    sx={{ mb: 1, lineHeight: 1.4 }}
                  >
                    ✓ Secure document management
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    color="text.secondary" 
                    sx={{ mb: 1, lineHeight: 1.4 }}
                  >
                    ✓ Multi-language support
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    ✓ Real-time data updates
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};

export default AdminDashboard;